import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Repeat2, Bell, Share2, Copy, Check } from "lucide-react";

/**
 * Repost / Follow / Share action bar — Option 6 (X-style spaced icons w/ hover glow).
 * `link` is an app-relative path to the post; share uses the absolute URL.
 */
export default function PostActions({ postType, postId, link = "", accent = "#ff4500", initial = {}, shareText = "", leading = null, dense = false }) {
    const { user } = useAuth();
    const [reposted, setReposted] = useState(!!initial.i_reposted);
    const [repostCount, setRepostCount] = useState(initial.repost_count || 0);
    const [following, setFollowing] = useState(!!initial.i_follow);
    const [followCount, setFollowCount] = useState(initial.follow_count || 0);
    const [busy, setBusy] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [repostOpen, setRepostOpen] = useState(false);
    const [quoteText, setQuoteText] = useState("");
    const [copied, setCopied] = useState(false);
    const shareRef = useRef(null);
    const repostRef = useRef(null);

    const absUrl = link ? `${window.location.origin}${link}` : window.location.href;

    useEffect(() => {
        const onDoc = (e) => {
            if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
            if (repostRef.current && !repostRef.current.contains(e.target)) setRepostOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const doRepost = async (comment) => {
        if (!user) return toast.error("Log in to repost");
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/social/repost", { post_type: postType, post_id: postId, comment: comment || undefined });
            setReposted(data.reposted);
            setRepostCount(data.repost_count);
            setRepostOpen(false);
            setQuoteText("");
            toast.success(comment ? "Quote reposted to your wall" : data.reposted ? "Reposted to your wall" : "Repost removed");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not repost");
        } finally { setBusy(false); }
    };

    const follow = async () => {
        if (!user) return toast.error("Log in to follow");
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post("/social/follow-post", { post_type: postType, post_id: postId });
            setFollowing(data.following);
            setFollowCount(data.follow_count);
            toast.success(data.following ? "Following this post" : "Unfollowed");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update");
        } finally { setBusy(false); }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(absUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Could not copy");
        }
    };

    const enc = encodeURIComponent(absUrl);
    const encText = encodeURIComponent(shareText || "Check this out");
    const socials = [
        { key: "x", label: "Share on X", href: `https://twitter.com/intent/tweet?url=${enc}&text=${encText}` },
        { key: "facebook", label: "Share on Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc}` },
        { key: "whatsapp", label: "Share on WhatsApp", href: `https://wa.me/?text=${encText}%20${enc}` },
    ];

    return (
        <div className={`flex items-center text-[#a0a0a0] ${dense ? "gap-1" : "mt-3 gap-8"}`} data-testid={`post-actions-${postId}`}>
            {leading}
            {/* Repost */}
            <div className="relative" ref={repostRef}>
                <button
                    type="button"
                    onClick={() => setRepostOpen((o) => !o)}
                    data-testid={`repost-btn-${postId}`}
                    className="group inline-flex items-center gap-1.5 text-[13px] transition-colors"
                    style={reposted ? { color: "#22c55e" } : undefined}
                    title="Repost or quote"
                >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full transition-colors group-hover:bg-[#22c55e]/15 group-hover:text-[#22c55e]">
                        <Repeat2 className="h-[18px] w-[18px]" />
                    </span>
                    {repostCount > 0 && <span>{repostCount}</span>}
                </button>
                {repostOpen && (
                    <div className="absolute left-0 z-30 mt-1 w-64 border border-[#2a2a2a] bg-[#0f0f0f] p-2 shadow-2xl" data-testid={`repost-menu-${postId}`}>
                        <button type="button" onClick={() => doRepost("")} data-testid={`repost-plain-${postId}`}
                            className="flex w-full items-center gap-2 px-2 py-2 text-left text-xs text-white hover:bg-[#1a1a1a]">
                            <Repeat2 className="h-3.5 w-3.5 text-[#22c55e]" /> {reposted ? "Undo repost" : "Repost"}
                        </button>
                        <div className="border-t border-[#2a2a2a] my-1" />
                        <textarea
                            data-testid={`quote-input-${postId}`}
                            value={quoteText}
                            onChange={(e) => setQuoteText(e.target.value)}
                            placeholder="Add a comment…"
                            rows={2}
                            maxLength={5000}
                            className="w-full resize-none bg-[#141414] border border-[#2a2a2a] px-2 py-1.5 text-sm text-white outline-none focus:border-[#22c55e]"
                        />
                        <button type="button" disabled={!quoteText.trim() || busy} onClick={() => doRepost(quoteText.trim())} data-testid={`quote-submit-${postId}`}
                            className="mt-1 w-full rounded-full bg-[#22c55e] py-1.5 text-xs font-bold text-white disabled:opacity-40 hover:bg-[#16a34a]">
                            Quote
                        </button>
                    </div>
                )}
            </div>

            {/* Follow */}
            <button
                type="button"
                onClick={follow}
                data-testid={`follow-post-btn-${postId}`}
                className="group inline-flex items-center gap-1.5 text-[13px] transition-colors"
                style={following ? { color: accent } : undefined}
                title="Follow this post for new replies"
            >
                <span className="flex h-8 w-8 items-center justify-center rounded-full transition-colors group-hover:bg-[color:var(--acc)]/15" style={{ "--acc": accent }}>
                    <Bell className="h-[18px] w-[18px]" fill={following ? accent : "none"} />
                </span>
                {followCount > 0 && <span style={following ? { color: accent } : undefined}>{followCount}</span>}
            </button>

            {/* Share */}
            <div className="relative" ref={shareRef}>
                <button
                    type="button"
                    onClick={() => setShareOpen((o) => !o)}
                    data-testid={`share-btn-${postId}`}
                    className="group inline-flex items-center text-[13px] transition-colors"
                    title="Share"
                >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full transition-colors group-hover:bg-[#2196f3]/15 group-hover:text-[#2196f3]">
                        <Share2 className="h-[18px] w-[18px]" />
                    </span>
                </button>
                {shareOpen && (
                    <div className="absolute left-0 z-30 mt-1 w-48 border border-[#2a2a2a] bg-[#0f0f0f] py-1 shadow-2xl" data-testid={`share-menu-${postId}`}>
                        <button type="button" onClick={copyLink} data-testid={`share-copy-${postId}`} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-white hover:bg-[#1a1a1a]">
                            {copied ? <Check className="h-3.5 w-3.5 text-[#22c55e]" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied!" : "Copy link"}
                        </button>
                        {socials.map((s) => (
                            <a key={s.key} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.key}-${postId}`}
                                onClick={() => setShareOpen(false)}
                                className="block px-3 py-2 text-xs text-white hover:bg-[#1a1a1a]">
                                {s.label}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
